import {
    Activity,
    AlertTriangle,
    Archive,
    BarChart3,
    Building2,
    ChevronDown,
    ChevronRight,
    Coins,
    CreditCard,
    Download,
    FileText,
    Image,
    KeyRound,
    LayoutDashboard,
    LogOut,
    Menu,
    MonitorPlay,
    Percent,
    Package,
    Puzzle,
    Receipt,
    Settings,
    ShieldCheck,
    ShieldAlert,
    Sliders,
    Smartphone,
    Store,
    Tag,
    TrendingUp,
    User,
    UserCog,
    Users,
    Wallet,
    X
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import { ADMIN_PASSWORD_HELP, getAdminPasswordError } from '../utils/adminPasswordPolicy';
import './Sidebar.css';

const Sidebar = () => {
    const location = useLocation();
    const { logout, admin, updateProfile } = useAdminAuth();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [invoicesExpanded, setInvoicesExpanded] = useState(false);
    const [configExpanded, setConfigExpanded] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const [profileSaving, setProfileSaving] = useState(false);
    const [profileError, setProfileError] = useState('');
    const [profileSuccess, setProfileSuccess] = useState('');
    const [passwordValidationError, setPasswordValidationError] = useState('');
    const [passwordChangedModalOpen, setPasswordChangedModalOpen] = useState(false);
    const [profileForm, setProfileForm] = useState({
        firstName: '',
        lastName: '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        mfaCode: ''
    });
    const [operationsExpanded, setOperationsExpanded] = useState(() => [
        '/admin/device-activation-support',
        '/admin/updates',
        '/admin/operational-alerts',
        '/admin/audit-log',
        '/admin/events',
        '/admin/kiosk-mode'
    ].some(path => location.pathname.startsWith(path)));

    const closeMobileSidebar = () => {
        if (window.matchMedia('(max-width: 768px)').matches) {
            setIsCollapsed(false);
        }
    };

    useEffect(() => {
        if (window.matchMedia('(max-width: 768px)').matches) {
            setIsCollapsed(false);
        }
    }, [location.pathname]);

    const menuItems = [
        {
            path: '/admin/overview',
            icon: LayoutDashboard,
            label: 'Overview',
            badge: null
        },
        {
            path: '/admin/tenants',
            icon: Building2,
            label: 'Tenants',
            badge: null
        },
        {
            path: '/admin/venues',
            icon: Store,
            label: 'Venues',
            badge: null
        },
        {
            path: '/admin/devices',
            icon: Smartphone,
            label: 'Devices',
            badge: null
        },
        {
            path: '/admin/tip-transactions',
            icon: TrendingUp,
            label: 'Transfers',
            badge: null
        }
    ];

    const secondaryMenuItems = [
        {
            path: '/admin/workers',
            icon: Users,
            label: 'Workers',
            badge: null
        },
        {
            path: '/admin/commercial-offers',
            icon: Coins,
            label: 'Offerte Commerciali',
            badge: null
        },
        {
            path: '/admin/commercial-commissions',
            icon: Percent,
            label: 'Provvigioni',
            badge: null
        },
        {
            path: '/admin/seller-portal',
            icon: UserCog,
            label: 'Seller Portal',
            badge: null
        },
        {
            path: '/admin/safety-payouts',
            icon: ShieldAlert,
            label: 'Safety Payouts',
            badge: null
        },
        {
            path: '/admin/worker-payouts',
            icon: Wallet,
            label: 'Worker Payout',
            badge: null
        }
    ];

    const operationsSubItems = [
        {
            path: '/admin/device-activation-support',
            icon: KeyRound,
            label: 'Activation Support'
        },
        {
            path: '/admin/updates',
            icon: Download,
            label: 'App Updates'
        },
        {
            path: '/admin/operational-alerts',
            icon: AlertTriangle,
            label: 'Centro Operativo'
        },
        {
            path: '/admin/audit-log',
            icon: ShieldCheck,
            label: 'Admin Audit Log'
        },
        {
            path: '/admin/events',
            icon: Activity,
            label: 'Eventi'
        },
        {
            path: '/admin/kiosk-mode',
            icon: MonitorPlay,
            label: 'Kiosk Mode'
        },
        {
            path: '/admin/legacy-tenants',
            icon: Archive,
            label: 'Tenant Legacy Features'
        }
    ];

    const invoiceSubItems = [
        {
            path: '/admin/invoices/monitoring',
            icon: Activity,
            label: 'Monitoraggio'
        },
        {
            path: '/admin/invoices',
            icon: Receipt,
            label: 'Lista Fatture'
        },
        {
            path: '/admin/invoices/generate',
            icon: FileText,
            label: 'Genera Fatture'
        },
        {
            path: '/admin/credit-notes',
            icon: CreditCard,
            label: 'Note di Credito'
        }
    ];

    const configSubItems = [
        {
            path: '/admin/global-settings',
            icon: Settings,
            label: 'Global Settings'
        },
        {
            path: '/admin/remote-device-config',
            icon: Sliders,
            label: 'Kiosk Remote Config'
        },
        {
            path: '/admin/media',
            icon: Image,
            label: 'Media Assets'
        },
        {
            path: '/admin/venue-categories',
            icon: Tag,
            label: 'Venue Categories'
        },
        {
            path: '/admin/profile-roles',
            icon: User,
            label: 'Profile Roles'
        },
        {
            path: '/admin/plan-features',
            icon: Puzzle,
            label: 'Plan Features'
        },
        {
            path: '/admin/plans',
            icon: Package,
            label: 'Piani'
        },
        {
            path: '/admin/legal-documents',
            icon: FileText,
            label: 'Legal Documents'
        }
    ];

    const isActive = (path) => {
        if (path === '/admin/analytics') {
            return location.pathname.startsWith('/admin/analytics');
        }
        if (path === '/admin/tip-transactions') {
            return location.pathname.startsWith('/admin/tip-transactions');
        }
        return location.pathname === path;
    };

    const isOperationsSectionActive = () => {
        return operationsSubItems.some(item => location.pathname.startsWith(item.path));
    };

    const isInvoicesSectionActive = () => {
        return location.pathname.startsWith('/admin/invoices') ||
            location.pathname.startsWith('/admin/credit-notes');
    };

    const isConfigSectionActive = () => {
        return location.pathname.startsWith('/admin/global-settings') ||
            location.pathname.startsWith('/admin/remote-device-config') ||
            location.pathname.startsWith('/admin/media') ||
            location.pathname.startsWith('/admin/venue-categories') ||
            location.pathname.startsWith('/admin/profile-roles') ||
            location.pathname.startsWith('/admin/plan-features') ||
            location.pathname.startsWith('/admin/plans') ||
            location.pathname.startsWith('/admin/legal-documents');
    };

    const handleLogout = async () => {
        if (window.confirm('Sei sicuro di voler uscire?')) {
            await logout();
        }
    };

    const openProfile = () => {
        setProfileForm({
            firstName: admin?.firstName || '',
            lastName: admin?.lastName || '',
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
            mfaCode: ''
        });
        setProfileError('');
        setProfileSuccess('');
        setPasswordValidationError('');
        setProfileOpen(true);
    };

    const handleProfileSubmit = async (event) => {
        event.preventDefault();
        setProfileError('');
        setProfileSuccess('');
        setPasswordValidationError('');
        if (profileForm.newPassword) {
            const passwordError = getAdminPasswordError(profileForm.newPassword);
            if (passwordError) {
                setPasswordValidationError(passwordError);
                return;
            }
        }
        if (profileForm.newPassword && profileForm.newPassword !== profileForm.confirmPassword) {
            setPasswordValidationError('Le nuove password non coincidono.');
            return;
        }

        const hasNameChanges =
            profileForm.firstName !== (admin?.firstName || '') ||
            profileForm.lastName !== (admin?.lastName || '');
        const payload = {};
        if (hasNameChanges) {
            payload.firstName = profileForm.firstName;
            payload.lastName = profileForm.lastName;
        }
        if (profileForm.newPassword) {
            payload.currentPassword = profileForm.currentPassword;
            payload.newPassword = profileForm.newPassword;
            payload.mfaCode = profileForm.mfaCode;
        }
        if (Object.keys(payload).length === 0) {
            setProfileError('Non ci sono modifiche da salvare.');
            return;
        }

        setProfileSaving(true);
        try {
            await updateProfile(payload);
            const passwordChanged = Boolean(profileForm.newPassword);
            setProfileForm((current) => ({
                ...current,
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
                mfaCode: ''
            }));
            if (passwordChanged) {
                setProfileOpen(false);
                setProfileSuccess('');
                setPasswordChangedModalOpen(true);
            } else {
                setProfileSuccess('Profilo aggiornato.');
            }
        } catch (error) {
            setProfileError(error.message || 'Impossibile aggiornare il profilo.');
        } finally {
            setProfileSaving(false);
        }
    };

    return (
        <>
            {/* Mobile Overlay */}
            {isCollapsed && (
                <div
                    className="sidebar-overlay"
                    onClick={() => setIsCollapsed(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`sidebar ${isCollapsed ? 'sidebar-mobile-open' : ''}`}>
                <div className="sidebar-header">
                    <div className="sidebar-logo">
                        <img
                            src="/soontip_logo_compact_white.svg"
                            alt="SoonTip Logo"
                            className="logo-icon"
                        />
                        {!isCollapsed && <span className="logo-text">SoonTip Admin</span>}
                    </div>
                    <button
                        className="sidebar-toggle"
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        aria-label="Toggle sidebar"
                    >
                        {isCollapsed ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>

                <nav className="sidebar-nav">
                    {menuItems.map((item) => {
                        const Icon = item.icon;
                        const active = isActive(item.path);

                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`sidebar-item ${active ? 'active' : ''}`}
                                onClick={closeMobileSidebar}
                            >
                                <Icon size={20} className="sidebar-icon" />
                                <span className="sidebar-label">{item.label}</span>
                                {item.badge && (
                                    <span className="sidebar-badge">{item.badge}</span>
                                )}
                            </Link>
                        );
                    })}

                    <Link
                        to="/admin/workers"
                        className={`sidebar-item ${isActive('/admin/workers') ? 'active' : ''}`}
                        onClick={closeMobileSidebar}
                    >
                        <Users size={20} className="sidebar-icon" />
                        <span className="sidebar-label">Workers</span>
                    </Link>

                    <Link
                        to="/admin/analytics"
                        className={`sidebar-item ${isActive('/admin/analytics') ? 'active' : ''}`}
                        onClick={closeMobileSidebar}
                    >
                        <BarChart3 size={20} className="sidebar-icon" />
                        <span className="sidebar-label">Analytics</span>
                    </Link>

                    <div className="sidebar-group-label">Business</div>

                    {secondaryMenuItems.slice(1).map((item) => {
                        const Icon = item.icon;
                        const active = isActive(item.path);

                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`sidebar-item ${active ? 'active' : ''}`}
                                onClick={closeMobileSidebar}
                            >
                                <Icon size={20} className="sidebar-icon" />
                                <span className="sidebar-label">{item.label}</span>
                                {item.badge && (
                                    <span className="sidebar-badge">{item.badge}</span>
                                )}
                            </Link>
                        );
                    })}

                    {/* Invoices Section with Submenu */}
                    <div className="sidebar-section">
                        <button
                            className={`sidebar-item ${isInvoicesSectionActive() ? 'active' : ''}`}
                            onClick={() => setInvoicesExpanded(!invoicesExpanded)}
                        >
                            <Receipt size={20} className="sidebar-icon" />
                            <span className="sidebar-label">Fatturazione</span>
                            {invoicesExpanded ? (
                                <ChevronDown size={16} className="sidebar-chevron" />
                            ) : (
                                <ChevronRight size={16} className="sidebar-chevron" />
                            )}
                        </button>

                        {invoicesExpanded && (
                            <div className="sidebar-submenu">
                                {invoiceSubItems.map((subItem) => {
                                    const SubIcon = subItem.icon;
                                    const active = isActive(subItem.path);

                                    return (
                                        <Link
                                            key={subItem.path}
                                            to={subItem.path}
                                            className={`sidebar-subitem ${active ? 'active' : ''}`}
                                            onClick={closeMobileSidebar}
                                        >
                                            <SubIcon size={18} className="sidebar-icon" />
                                            <span className="sidebar-label">{subItem.label}</span>
                                        </Link>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="sidebar-section">
                        <button
                            className={`sidebar-item ${isOperationsSectionActive() ? 'active' : ''}`}
                            onClick={() => setOperationsExpanded(!operationsExpanded)}
                        >
                            <Activity size={20} className="sidebar-icon" />
                            <span className="sidebar-label">Operations</span>
                            {operationsExpanded ? (
                                <ChevronDown size={16} className="sidebar-chevron" />
                            ) : (
                                <ChevronRight size={16} className="sidebar-chevron" />
                            )}
                        </button>

                        {operationsExpanded && (
                            <div className="sidebar-submenu">
                                {operationsSubItems.map((subItem) => {
                                    const SubIcon = subItem.icon;
                                    const active = isActive(subItem.path);

                                    return (
                                        <Link
                                            key={subItem.path}
                                            to={subItem.path}
                                            className={`sidebar-subitem ${active ? 'active' : ''}`}
                                            onClick={closeMobileSidebar}
                                        >
                                            <SubIcon size={18} className="sidebar-icon" />
                                            <span className="sidebar-label">{subItem.label}</span>
                                        </Link>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="sidebar-section">
                        <button
                            className={`sidebar-item ${isConfigSectionActive() ? 'active' : ''}`}
                            onClick={() => setConfigExpanded(!configExpanded)}
                        >
                            <Settings size={20} className="sidebar-icon" />
                            <span className="sidebar-label">Configurazioni</span>
                            {configExpanded ? (
                                <ChevronDown size={16} className="sidebar-chevron" />
                            ) : (
                                <ChevronRight size={16} className="sidebar-chevron" />
                            )}
                        </button>

                        {configExpanded && (
                            <div className="sidebar-submenu">
                                {configSubItems.map((subItem) => {
                                    const SubIcon = subItem.icon;
                                    const active = isActive(subItem.path);

                                    return (
                                        <Link
                                            key={subItem.path}
                                            to={subItem.path}
                                            className={`sidebar-subitem ${active ? 'active' : ''}`}
                                            onClick={closeMobileSidebar}
                                        >
                                            <SubIcon size={18} className="sidebar-icon" />
                                            <span className="sidebar-label">{subItem.label}</span>
                                        </Link>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </nav>

                <div className="sidebar-footer">
                    {admin && (
                        <div className="sidebar-user">
                            <button
                                type="button"
                                className="user-info sidebar-profile-trigger"
                                onClick={openProfile}
                                aria-label="Modifica profilo e password"
                            >
                                <span className="user-icon"><User size={16} /></span>
                                <span className="user-copy">
                                    <strong>{[admin.firstName, admin.lastName].filter(Boolean).join(' ') || 'Amministratore'}</strong>
                                    <span className="user-email">{admin.email}</span>
                                </span>
                            </button>
                        </div>
                    )}

                    <button
                        className="sidebar-logout"
                        onClick={handleLogout}
                    >
                        <LogOut size={18} />
                        <span>Logout</span>
                    </button>

                    <div className="sidebar-version">
                        <small>v1.0.0</small>
                    </div>
                </div>
            </aside>

            {profileOpen && (
                <div className="admin-profile-backdrop" onMouseDown={(event) => {
                    if (event.target === event.currentTarget && !profileSaving) setProfileOpen(false);
                }}>
                    <section
                        className="admin-profile-dialog"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="admin-profile-title"
                    >
                        <div className="admin-profile-heading">
                            <div>
                                <h2 id="admin-profile-title">Profilo amministratore</h2>
                                <p>{admin.email}</p>
                            </div>
                            <button
                                type="button"
                                className="admin-profile-close"
                                onClick={() => setProfileOpen(false)}
                                disabled={profileSaving}
                                aria-label="Chiudi"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleProfileSubmit} className="admin-profile-form">
                            <div className="admin-profile-name-fields">
                                <label>
                                    Nome <span>(facoltativo)</span>
                                    <input
                                        value={profileForm.firstName}
                                        onChange={(event) => setProfileForm({ ...profileForm, firstName: event.target.value })}
                                        maxLength={100}
                                        autoComplete="given-name"
                                    />
                                </label>
                                <label>
                                    Cognome <span>(facoltativo)</span>
                                    <input
                                        value={profileForm.lastName}
                                        onChange={(event) => setProfileForm({ ...profileForm, lastName: event.target.value })}
                                        maxLength={100}
                                        autoComplete="family-name"
                                    />
                                </label>
                            </div>

                            <div className="admin-profile-password-section">
                                <h3>Cambia password <span>(facoltativo)</span></h3>
                                <label>
                                    Password attuale
                                    <input
                                        type="password"
                                        value={profileForm.currentPassword}
                                        onChange={(event) => setProfileForm({ ...profileForm, currentPassword: event.target.value })}
                                        autoComplete="current-password"
                                    />
                                </label>
                                <label>
                                    Nuova password
                                    <input
                                        type="password"
                                        value={profileForm.newPassword}
                                        onChange={(event) => {
                                            const newPassword = event.target.value;
                                            setProfileForm({ ...profileForm, newPassword });
                                            if (newPassword.length >= 12) setPasswordValidationError('');
                                        }}
                                        maxLength={128}
                                        autoComplete="new-password"
                                        aria-invalid={!!passwordValidationError}
                                        aria-describedby="admin-new-password-help"
                                    />
                                    <small
                                        id="admin-new-password-help"
                                        className={passwordValidationError ? 'admin-profile-password-error' : ''}
                                    >
                                        {passwordValidationError || ADMIN_PASSWORD_HELP}
                                    </small>
                                </label>
                                <label>
                                    Conferma nuova password
                                    <input
                                        type="password"
                                        value={profileForm.confirmPassword}
                                        onChange={(event) => setProfileForm({ ...profileForm, confirmPassword: event.target.value })}
                                        autoComplete="new-password"
                                    />
                                </label>
                                <label>
                                    Codice dell’app autenticatrice
                                    <input
                                        value={profileForm.mfaCode}
                                        onChange={(event) => setProfileForm({ ...profileForm, mfaCode: event.target.value })}
                                        inputMode="numeric"
                                        autoComplete="one-time-code"
                                        maxLength={32}
                                    />
                                </label>
                            </div>

                            {profileError && <p className="admin-profile-message error">{profileError}</p>}
                            {profileSuccess && <p className="admin-profile-message success">{profileSuccess}</p>}

                            <div className="admin-profile-actions">
                                <button
                                    type="button"
                                    className="admin-profile-cancel"
                                    onClick={() => setProfileOpen(false)}
                                    disabled={profileSaving}
                                >
                                    Chiudi
                                </button>
                                <button type="submit" className="admin-profile-save" disabled={profileSaving}>
                                    {profileSaving ? 'Salvataggio...' : 'Salva modifiche'}
                                </button>
                            </div>
                        </form>
                    </section>
                </div>
            )}

            {passwordChangedModalOpen && (
                <div className="admin-success-backdrop">
                    <section
                        className="admin-success-dialog"
                        role="alertdialog"
                        aria-modal="true"
                        aria-labelledby="admin-password-success-title"
                        aria-describedby="admin-password-success-description"
                    >
                        <div className="admin-success-icon">
                            <KeyRound size={26} />
                        </div>
                        <p className="admin-success-eyebrow">SICUREZZA ACCOUNT</p>
                        <h2 id="admin-password-success-title">Password aggiornata</h2>
                        <p id="admin-password-success-description">
                            La nuova password è stata salvata correttamente. Per sicurezza, le altre sessioni di questo account sono state disconnesse.
                        </p>
                        <button
                            type="button"
                            className="admin-success-confirm"
                            onClick={() => setPasswordChangedModalOpen(false)}
                            autoFocus
                        >
                            OK
                        </button>
                    </section>
                </div>
            )}

            {/* Mobile Toggle Button (visible only on mobile when sidebar is closed) */}
            {!isCollapsed && (
                <button
                    className="mobile-menu-toggle"
                    onClick={() => setIsCollapsed(true)}
                    aria-label="Open menu"
                >
                    <Menu size={24} />
                </button>
            )}
        </>
    );
};

export default Sidebar;
